import { useCallback, useEffect } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { useScheduleBookingRequestsShallowStore } from '../../../../../../stores/use-schedule-booking-requests-store';
import { useCombinedScheduleRequest } from '../hooks/use-combined-requests';
import { useGetScheduleRequestInfiniteListQuery } from '../hooks/useGetScheduleRequestInfiniteListQuery';

export const useSchedulePulseRequestList = ({ locationId }: { locationId: string }) => {
  const { t } = useTranslation('scheduleCalendarActions');

  const alert = useAlert();
  const { combineBookingRequests } = useScheduleBookingRequestsShallowStore('combineBookingRequests');
  const combinedBookingListQuery = useCombinedScheduleRequest(locationId, combineBookingRequests);
  const scheduleRequestListQuery = useGetScheduleRequestInfiniteListQuery(locationId, combineBookingRequests);
  const combineBookingRequestsRefetch = combinedBookingListQuery.refetchQueries;
  const scheduleRequestListRefetch = scheduleRequestListQuery.refetch;
  const refetchScheduleRequestList = useCallback(
    () => (combineBookingRequests ? combineBookingRequestsRefetch() : scheduleRequestListRefetch()),
    [combineBookingRequests, combineBookingRequestsRefetch, scheduleRequestListRefetch]
  );

  useEffect(() => {
    if (scheduleRequestListQuery.isError) {
      alert.error(t('Failed to load appointment request list'));
    }
  }, [alert, scheduleRequestListQuery.isError, t]);

  const isEmptyScheduleRequestList = combineBookingRequests
    ? combinedBookingListQuery?.data?.pages?.[0]?.rows?.length
    : !scheduleRequestListQuery?.data?.pages?.[0]?.rows?.length;

  const infiniteListQuery = combineBookingRequests ? combinedBookingListQuery : scheduleRequestListQuery;

  return { refetchScheduleRequestList, isEmptyScheduleRequestList, infiniteListQuery };
};
